import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { TextInput } from "../../../components/textInput";
import { SideSheet } from "../../../components/sheet/sideSheet";
import { Toast } from "../../../components/toast";
import { DeleteConfirm } from "../../../components/modal/deleteConfirm";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PlusIcon } from "@heroicons/react/24/outline";
import { TextArea } from "../../../components/textArea";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { ActivitiesList } from "./activitiesList";
import { ActivityDetails } from "./activityDetails";
import { TemplateSelection } from "./templateSelection";
import { GeofenceStatus } from "./geofenceStatus";
import { SchedulingCustomerFilter } from "../schedules/filters/customerFilter";
import Datepicker from "react-tailwindcss-datepicker";
import { MultiSelect } from "../../../components/menu/multiSelect";
import { isShiftInPast } from "../../../utils/dateTimeUtil";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";

// Resolve the site clock-in is checked at: the first activity that carries
// one (activities may store it nested as `site` or flat as
// site_id/site_name from the picker, or siteId/siteName on load).
function resolvePrimarySite(activities) {
  for (const a of activities || []) {
    const id = a?.site?.id ?? a?.site_id ?? a?.siteId ?? null;
    const name =
      a?.site?.name ?? a?.site_name ?? a?.siteName ?? null;
    if (id) return { id, name };
  }
  return null;
}

// Helper to check if a date is in the past
function isDateInPast(dateString) {
  if (!dateString) return false;
  // Parse input as local midnight
  const [year, month, day] = dateString.split("-").map(Number);
  const inputDate = new Date(year, month - 1, day);
  // Get today's date in local midnight
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return inputDate < todayLocal;
}

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  date: z
    .string()
    .optional()
    .refine((date) => {
      if (!date) return true; // Optional field
      // Ensure date is in YYYY-MM-DD format
      const formatted = date.length > 10 ? date.slice(0, 10) : date;
      return !isDateInPast(formatted);
    }, "Cannot select a date in the past"),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  instructions: z.string().optional(),
  require_geofence: z.boolean().optional(),
  post_id: z.string().optional(),
  saveAsTemplate: z.boolean().optional(),
});

// Turns a post's activities (rules) into timed shift activities. "Once"
// activities land at the shift start; "every N hours" repeat from the start until
// the end. Each activity is 15 minutes. Times are local "HH:MM"; overnight
// shifts wrap past midnight.
const ACTIVITY_MINUTES = 15;
function postActivitiesToShift(rules, startTime, endTime) {
  const toMin = (t) => {
    const [h, m] = (t || "00:00").split(":").map(Number);
    return h * 60 + m;
  };
  const toTime = (mins) => {
    const m = ((mins % 1440) + 1440) % 1440;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  };
  const start = toMin(startTime);
  let end = toMin(endTime);
  if (end <= start) end += 1440;

  const out = [];
  rules.forEach((rule, d) => {
    const step = rule.frequency_hours ? rule.frequency_hours * 60 : null;
    for (let t = start; t + ACTIVITY_MINUTES <= end; t += step || Infinity) {
      out.push({
        id: Date.now() + d * 1000 + out.length, // frontend-only id
        activityId: rule.activity_id,
        type: rule.activity_type,
        label: rule.label,
        adHoc: false,
        start: toTime(t),
        end: toTime(t + ACTIVITY_MINUTES),
        duration: ACTIVITY_MINUTES,
        siteId: rule.site?.id ?? null,
        siteName: rule.site?.name ?? null,
      });
      if (!step) break;
    }
  });
  return out.sort((a, b) => {
    const rel = (x) => (toMin(x.start) - start + 1440) % 1440;
    return rel(a) - rel(b);
  });
}

// "" in the form means no post.
const postIdPayload = (value) => (value ? Number(value) : null);

// True when nothing is picked (All) or the customer is one of the picked ones.
const matchesCustomers = (customerIds, customerId) =>
  customerIds.length === 0 ||
  customerIds.some((id) => String(id) === String(customerId));

// Dropdown options: site posts as "Customer · Site · Post", then patrol routes.
// With customers picked, only their posts show (patrol routes have no
// customer, so they drop out). The current post always stays listed.
function postOptions(posts, customerIds = [], currentPostId = "") {
  const visible = posts.filter(
    (p) =>
      String(p.id) === String(currentPostId) ||
      (customerIds.length === 0
        ? true
        : p.site && matchesCustomers(customerIds, p.customer?.id)),
  );
  const sitePosts = visible.filter((p) => p.site);
  const routes = visible.filter((p) => !p.site);
  return [
    { value: "", label: "No post" },
    ...sitePosts.map((p) => ({
      value: String(p.id),
      label: [p.customer?.name, p.site.name, p.name].filter(Boolean).join(" · "),
    })),
    ...routes.map((p) => ({
      value: String(p.id),
      label: `Patrol route · ${p.name}`,
    })),
  ];
}

// Helper to generate 15-min increment options
function generateTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      // 24-hour clock: the label is the value.
      const value = `${h.toString().padStart(2, "0")}:${m
        .toString()
        .padStart(2, "0")}`;
      options.push({ label: value, value });
    }
  }
  return options;
}
const TIME_OPTIONS = generateTimeOptions();

// {value, label} options in the shape MultiSelect expects ({id, name}).
const toMenuOptions = (options) =>
  options.map((o) => ({ id: o.value, name: o.label, disabled: o.disabled }));

// Helper to parse time string ("HH:mm") to minutes
function timeToMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

// Helper to check if a shift spans overnight (end time is before start time)
function isOvernightShift(startTime, endTime) {
  const startMins = timeToMinutes(startTime);
  const endMins = timeToMinutes(endTime);
  return endMins < startMins;
}

// Helper to check if a time is within shift bounds (handles overnight shifts)
function isTimeInShiftBounds(time, startTime, endTime) {
  const timeMins = timeToMinutes(time);
  const startMins = timeToMinutes(startTime);
  const endMins = timeToMinutes(endTime);

  if (isOvernightShift(startTime, endTime)) {
    // For overnight shifts: time must be >= start OR <= end
    return timeMins >= startMins || timeMins <= endMins;
  } else {
    // For regular shifts: time must be >= start AND <= end
    return timeMins >= startMins && timeMins <= endMins;
  }
}

// Helper to format hour for activities section
function formatHourLabel(h) {
  return `${h.toString().padStart(2, "0")}:00`;
}

// Helper to parse date string (YYYY-MM-DD) to Date object
function parseDateString(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}
// Helper to format Date object to YYYY-MM-DD
function formatDateToString(dateObj) {
  if (!dateObj) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Helper to robustly parse date and time from ISO string
function parseDateTime(dateTimeStr) {
  if (!dateTimeStr) return { date: "", time: "" };
  const dateObj = new Date(dateTimeStr);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const date = `${year}-${month}-${day}`;
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const time = `${hours}:${minutes}`;
  return { date, time };
}

// Helper to convert local time string to UTC ISO string
// Takes a date string in format "YYYY-MM-DDTHH:mm:ss" (local time) and converts to UTC
function convertLocalTimeToUTC(dateTimeStr) {
  if (!dateTimeStr) return null;
  // Create a Date object - it will interpret the string as local time (user's browser timezone)
  const localDate = new Date(dateTimeStr);
  // Return as ISO string (which is always in UTC)
  return localDate.toISOString();
}

function ShiftDetails({
  isOpen,
  toggleOpen,
  selectedShiftId,
  isAddMode,
  selectedDate,
  selectedUserId,
  onSuccess,
  isTemplateMode = false,
}) {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      date: "",
      startTime: "08:00",
      endTime: "17:00",
      instructions: "",
      saveAsTemplate: false,
      require_geofence: false,
      post_id: "",
    },
    resolver: zodResolver(schema),
  });

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  // Menu-driven fields aren't registered inputs, so set them on the form directly.
  const setFormValue = (name, value) =>
    setValue(name, value, { shouldDirty: true, shouldValidate: true });

  // Watch form values to check if shift time is in the past
  const watchedValues = watch();

  // Ref to track if we've already fetched data for this shift
  const hasFetchedRef = useRef(false);

  // Check if shift is in the past (cannot be changed)
  const [canChange, setCanChange] = useState(true);

  useEffect(() => {
    if (isOpen && isAddMode) {
      reset({
        name: "",
        date: selectedDate || "",
        startTime: "08:00",
        endTime: "17:00",
        instructions: "",
        saveAsTemplate: false,
        require_geofence: false,
        post_id: "",
      });
      // Reset date picker value for add mode
      setDateValue({
        startDate: selectedDate || "",
        endDate: selectedDate || "",
      });

      // For add mode, check if the selected date/time is in the past
      if (selectedDate) {
        // Use local time comparison to avoid timezone issues
        const today = new Date();
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth();
        const todayDay = today.getDate();
        const todayLocal = new Date(todayYear, todayMonth, todayDay);

        // Parse the selected date to get local date
        const [year, month, day] = selectedDate.split("-").map(Number);
        const selectedDateLocal = new Date(year, month - 1, day);

        if (selectedDateLocal < todayLocal) {
          // Past date - cannot add shift
          setCanChange(false);
        } else if (selectedDateLocal.getTime() === todayLocal.getTime()) {
          // Today - allow adding shifts regardless of current time
          setCanChange(true);
        } else {
          // Future date - can add shift
          setCanChange(true);
        }
      } else {
        // No date selected yet - allow changes
        setCanChange(true);
      }
    } else if (isOpen && !isAddMode && selectedDate && !isTemplateMode) {
      // For edit mode, use the current week start as the calendar starting point
      // but keep the original shift date for the form
      setDateValue({
        startDate: selectedDate,
        endDate: selectedDate,
      });
    }
  }, [isOpen, isAddMode, reset, selectedDate]);

  // Posts for the picker. Hidden while every site only has its default post
  // and there are no patrol routes, so small orgs never see it.
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!isOpen) return;
    authFetch(`${process.env.REACT_APP_API_URL}/v1/posts`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setPosts)
      .catch((error) => console.error("Error fetching posts", error));
  }, [isOpen]);
  const selectedPost = posts.find(
    (p) => String(p.id) === String(watchedValues.post_id || ""),
  );
  // Shown once an org has more than default posts, or any post has activities.
  const showPostPicker =
    posts.some((p) => !p.is_default || p.activity_count > 0) ||
    !!watchedValues.post_id;

  // Customers the shift is limited to. Empty means All, which shows every
  // post and activity like before. Not saved; it only narrows the pickers.
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  useEffect(() => {
    if (isOpen) setSelectedCustomers([]);
  }, [isOpen, selectedShiftId]);
  const handleSelectedCustomers = (optionId) => {
    setSelectedCustomers((currentSelected) =>
      currentSelected.includes(optionId)
        ? currentSelected.filter((item) => item !== optionId)
        : [...currentSelected, optionId],
    );
  };
  const clearSelectedCustomers = () => setSelectedCustomers([]);

  // Narrowing to customers the chosen post doesn't belong to clears the post.
  useEffect(() => {
    if (!selectedPost || selectedCustomers.length === 0) return;
    if (
      !selectedPost.site ||
      !matchesCustomers(selectedCustomers, selectedPost.customer?.id)
    ) {
      setValue("post_id", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers]);

  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [backendData, setBackendData] = useState(null);
  const [activities, setActivities] = useState([]);

  // The selected post's activities. A new shift with no activities yet picks
  // them up automatically; otherwise "Add activities" appends them on request.
  const [postActivities, setPostActivities] = useState([]);
  const autoAppliedPostRef = useRef(null);
  useEffect(() => {
    const postId = watchedValues.post_id;
    if (!isOpen || !postId) {
      setPostActivities([]);
      return;
    }
    let cancelled = false;
    authFetch(`${process.env.REACT_APP_API_URL}/v1/posts/${postId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((post) => {
        if (cancelled) return;
        const rules = post?.activities || [];
        setPostActivities(rules);
        if (
          isAddMode &&
          rules.length &&
          autoAppliedPostRef.current !== postId
        ) {
          autoAppliedPostRef.current = postId;
          setActivities((current) =>
            current.length
              ? current
              : postActivitiesToShift(
                  rules,
                  methods.getValues("startTime"),
                  methods.getValues("endTime"),
                ),
          );
        }
      })
      .catch((error) => console.error("Error fetching post activities", error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, watchedValues.post_id]);
  useEffect(() => {
    if (!isOpen) autoAppliedPostRef.current = null;
  }, [isOpen]);

  // Post activities not on the shift yet. Adding only these keeps the link
  // from stacking duplicates; once everything is on, the link goes away.
  const onShift = (rule) =>
    activities.some(
      (a) =>
        a.type === rule.activity_type &&
        String(a.activityId) === String(rule.activity_id),
    );
  const missingPostActivities = postActivities.filter((rule) => !onShift(rule));

  const addPostActivities = () =>
    setActivities((current) => [
      ...current,
      ...postActivitiesToShift(
        missingPostActivities,
        methods.getValues("startTime"),
        methods.getValues("endTime"),
      ),
    ]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState({
    id: null,
    identifier: "",
  });

  useEffect(() => {
    if (!isAddMode && selectedShiftId && !hasFetchedRef.current) {
      hasFetchedRef.current = true;

      // Use different endpoints for templates vs shifts
      const endpoint = isTemplateMode
        ? `${process.env.REACT_APP_API_URL}/v1/shift-templates/${selectedShiftId}`
        : `${process.env.REACT_APP_API_URL}/v1/shift-definitions/${selectedShiftId}`;

      authFetch(endpoint)
        .then((res) => res.json())
        .then((data) => {
          setBackendData(data);
          // Map backend activities to frontend shape
          setActivities(
            (data.activities || []).map((a) => ({
              id: a.id, // This is the activity ID
              activityId: a.activity_id, // This is the checkpoint/tour ID
              type: a.activity_type,
              label: a.label,
              adHoc: a.is_ad_hoc,
              start: a.start_time ? parseDateTime(a.start_time).time : null,
              end: a.end_time ? parseDateTime(a.end_time).time : null,
              duration: a.duration_minutes,
              checkpoints: a.checkpoints || null, // if present
              // Site the activity sits at (used to resolve the clock-in geofence)
              siteId: a.site?.id ?? a.site_id ?? null,
              siteName: a.site?.name ?? a.site_name ?? null,
              // add more fields as needed
            })),
          );

          if (isTemplateMode) {
            // For templates, parse the time from the template's start/end times
            const { time: startTime } = parseDateTime(data.start_time);
            const { time: endTime } = parseDateTime(data.end_time);
            reset({
              name: data.name || "",
              startTime: startTime,
              endTime: endTime,
              instructions: data.instructions || "",
              require_geofence: !!data.require_geofence,
              post_id: data.post_id ? String(data.post_id) : "",
            });
            // Don't set date picker value for templates
          } else {
            // For shifts, parse both date and time
            const { date: startDate, time: startTime } = parseDateTime(
              data.start_time,
            );
            const { time: endTime } = parseDateTime(data.end_time);
            reset({
              name: data.name || "",
              date: startDate,
              startTime: startTime,
              endTime: endTime,
              instructions: data.instructions || "",
              require_geofence: !!data.require_geofence,
              post_id: data.post_id ? String(data.post_id) : "",
            });
            // Set date picker value for shifts
            setDateValue({ startDate, endDate: startDate });

            // Check if shift is in the past (only for shifts, not templates)
            const shiftInPast = isShiftInPast(startDate, endTime);
            setCanChange(!shiftInPast);
          }
        })
        .catch((error) => {
          console.error("Error fetching data", error);
        });
    }
  }, [selectedShiftId, isAddMode, reset, isTemplateMode]);

  // Reset the fetch flag when switching between add/edit modes or changing shift
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [selectedShiftId, isAddMode, isTemplateMode]);

  useEffect(() => {
    if (isAddMode) {
      setActivities([]);
    }
  }, [isAddMode]);

  // Monitor form values in add mode to check if selected time is in the past
  useEffect(() => {
    if (isAddMode && isOpen && watchedValues.date && watchedValues.endTime) {
      // Use local time comparison to avoid timezone issues
      const today = new Date();
      const todayYear = today.getFullYear();
      const todayMonth = today.getMonth();
      const todayDay = today.getDate();
      const todayLocal = new Date(todayYear, todayMonth, todayDay);

      // Parse the watched date to get local date
      const [year, month, day] = watchedValues.date.split("-").map(Number);
      const watchedDateLocal = new Date(year, month - 1, day);

      if (watchedDateLocal < todayLocal) {
        // Past date - cannot add shift
        setCanChange(false);
      } else if (watchedDateLocal.getTime() === todayLocal.getTime()) {
        // Today - allow adding shifts regardless of current time
        setCanChange(true);
      } else {
        // Future date - can add shift
        setCanChange(true);
      }
    }
  }, [isAddMode, isOpen, watchedValues.date, watchedValues.endTime]);

  // Monitor form errors
  useEffect(() => {
    // Form errors are handled by the form validation
  }, [errors]);

  const handleResponse = (message, type) => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "" });
  };

  const onSubmit = (data) => {
    // Additional validation for date in past (double-check)
    if (!isTemplateMode && data.date && isDateInPast(data.date)) {
      handleResponse("Cannot select a date in the past", "danger");
      return;
    }

    if (isAddMode) {
      if (isTemplateMode) {
        createTemplate(data);
      } else {
        createShift(data);
      }
    } else {
      if (isTemplateMode) {
        updateTemplate(data);
      } else {
        updateShift(data);
      }
    }
  };

  const createTemplate = async (data) => {
    try {
      // Format activities for backend (without dates/times)
      const formattedActivities = activities.map((activity) => {
        let activityId = null;

        // Always use activityId if it exists (this is the actual checkpoint/tour ID)
        if (activity.activityId) {
          activityId = activity.activityId;
        } else if (activity.id && typeof activity.id === "number") {
          // For existing activities from the backend, check if this is a real backend ID
          if (activity.id < 1000000000) {
            // This is likely a real backend ID - use it as activity_id
            activityId = activity.id;
          } else {
            // This is a frontend-generated ID (large number), so it's a new activity
            // The activityId should contain the actual checkpoint/tour ID
            activityId = activity.activityId;
          }
        } else {
          activityId = null;
        }

        const formattedActivity = {
          id: activity.id,
          activity_type: activity.type,
          activity_id: activityId,
          label: activity.label,
          start_time: activity.start
            ? convertLocalTimeToUTC(`2000-01-01T${activity.start}:00`)
            : null,
          end_time: activity.end
            ? convertLocalTimeToUTC(`2000-01-01T${activity.end}:00`)
            : null,
          is_ad_hoc: activity.adHoc ? 1 : 0,
        };

        return formattedActivity;
      });

      // Convert template times to UTC (using placeholder date 2000-01-01)
      const startTimeUTC = convertLocalTimeToUTC(
        `2000-01-01T${data.startTime}:00`,
      );
      const endTimeUTC = convertLocalTimeToUTC(`2000-01-01T${data.endTime}:00`);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/shift-templates`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: data.name,
            instructions: data.instructions,
            require_geofence: data.require_geofence ? 1 : 0,
            post_id: postIdPayload(data.post_id),
            start_time: startTimeUTC,
            end_time: endTimeUTC,
            org_id: orgId,
            activities: formattedActivities,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create template");
      }

      const createdTemplate = await response.json();

      // Reset form and activities after successful creation
      reset({
        name: "",
        date: "",
        startTime: "08:00",
        endTime: "17:00",
        instructions: "",
        saveAsTemplate: false,
      });
      setActivities([]);
      setDateValue({ startDate: "", endDate: "" });

      onSuccess();
      handleResponse("Template created successfully", "success");
    } catch (error) {
      console.error("Error creating template:", error);
      handleResponse(`Failed to create template: ${error.message}`, "danger");
    }
  };

  const updateTemplate = async (data) => {
    try {
      // Format activities for backend (without dates/times)
      const formattedActivities = activities.map((activity) => {
        let activityId = null;

        // Always use activityId if it exists (this is the actual checkpoint/tour ID)
        if (activity.activityId) {
          activityId = activity.activityId;
        } else if (activity.id && typeof activity.id === "number") {
          // For existing activities from the backend, check if this is a real backend ID
          if (activity.id < 1000000000) {
            // This is likely a real backend ID - use it as activity_id
            activityId = activity.id;
          } else {
            // This is a frontend-generated ID (large number), so it's a new activity
            // The activityId should contain the actual checkpoint/tour ID
            activityId = activity.activityId;
          }
        } else {
          activityId = null;
        }

        const formattedActivity = {
          id: activity.id,
          activity_type: activity.type,
          activity_id: activityId,
          label: activity.label,
          start_time: activity.start
            ? convertLocalTimeToUTC(`2000-01-01T${activity.start}:00`)
            : null,
          end_time: activity.end
            ? convertLocalTimeToUTC(`2000-01-01T${activity.end}:00`)
            : null,
          is_ad_hoc: activity.adHoc ? 1 : 0,
        };

        return formattedActivity;
      });

      // Convert template times to UTC (using placeholder date 2000-01-01)
      const startTimeUTC = convertLocalTimeToUTC(
        `2000-01-01T${data.startTime}:00`,
      );
      const endTimeUTC = convertLocalTimeToUTC(`2000-01-01T${data.endTime}:00`);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/shift-templates/${selectedShiftId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: data.name,
            instructions: data.instructions,
            require_geofence: data.require_geofence ? 1 : 0,
            post_id: postIdPayload(data.post_id),
            start_time: startTimeUTC,
            end_time: endTimeUTC,
            activities: formattedActivities,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update template");
      }

      const updatedTemplate = await response.json();

      onSuccess();
      handleResponse("Template updated successfully", "success");
    } catch (error) {
      console.error("Error updating template:", error);
      handleResponse(`Failed to update template: ${error.message}`, "danger");
    }
  };

  const createShift = async (data) => {
    try {
      // Calculate week_start from the selected date
      // Parse the date string properly to avoid timezone issues
      const shiftDate = data.date || formatDateToString(new Date());

      // Format activities for backend
      const formattedActivities = activities.map((activity) => {
        let activityId = null;

        // Always use activityId if it exists (this is the actual checkpoint/tour ID)
        if (activity.activityId) {
          // This is an existing activity with a real checkpoint/tour ID
          activityId = activity.activityId;
        } else if (activity.id && typeof activity.id === "number") {
          // For existing activities from the backend, check if this is a real backend ID
          if (activity.id < 1000000000) {
            // This is likely a real backend ID - use it as activity_id
            activityId = activity.id;
          } else {
            // This is a frontend-generated ID (large number), so it's a new activity
            // The activityId should contain the actual checkpoint/tour ID
            activityId = activity.activityId;
          }
        } else {
          // This is a new activity, we'll need to handle this in the backend
          activityId = null;
        }

        const formattedActivity = {
          id: activity.id, // Preserve the original ID for existing activities
          activity_type: activity.type,
          activity_id: activityId,
          label: activity.label,
          start_time: activity.start
            ? convertLocalTimeToUTC(`${shiftDate}T${activity.start}:00`)
            : null,
          end_time: activity.end
            ? convertLocalTimeToUTC(`${shiftDate}T${activity.end}:00`)
            : null,
          is_ad_hoc: activity.adHoc ? 1 : 0,
        };

        return formattedActivity;
      });

      // Calculate week_start from the selected date
      const [year, month, day] = shiftDate.split("-").map(Number);
      const selectedDate = new Date(year, month - 1, day); // month is 0-indexed
      selectedDate.setHours(0, 0, 0, 0);

      // Get the day of the week (0 = Sunday, 1 = Monday, etc.)
      const dayOfWeek = selectedDate.getDay();

      // Find the Sunday that starts this week
      // Use a more robust method that handles month boundaries correctly
      if (dayOfWeek !== 0) {
        // Calculate days to subtract to get to the previous Sunday
        const daysToSubtract = dayOfWeek;
        selectedDate.setDate(selectedDate.getDate() - daysToSubtract);
      }

      const week_start = selectedDate.toISOString().split("T")[0];

      // Convert local times to UTC before sending to backend
      const startTimeUTC = convertLocalTimeToUTC(
        `${shiftDate}T${data.startTime}:00`,
      );
      const endTimeUTC = convertLocalTimeToUTC(
        `${shiftDate}T${data.endTime}:00`,
      );

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/shift-definitions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: data.name,
            instructions: data.instructions,
            require_geofence: data.require_geofence ? 1 : 0,
            post_id: postIdPayload(data.post_id),
            start_time: startTimeUTC,
            end_time: endTimeUTC,
            user_id: selectedUserId,
            org_id: orgId,
            week_start: week_start,
            activities: formattedActivities,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create shift");
      }

      const createdShift = await response.json();

      // If user checked "save as template", create a template from this shift
      if (data.saveAsTemplate) {
        try {
          const templateResponse = await authFetch(
            `${process.env.REACT_APP_API_URL}/v1/shift-definitions/${createdShift.id}/save-as-template`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: data.name,
                instructions: data.instructions,
                require_geofence: data.require_geofence ? 1 : 0,
                post_id: postIdPayload(data.post_id),
              }),
            },
          );

          if (templateResponse.ok) {
            const createdTemplate = await templateResponse.json();
            handleResponse(
              "Shift created and saved as template successfully",
              "success",
            );
          } else {
            console.warn("ShiftDetails: Failed to create template from shift");
            handleResponse("Shift created successfully", "success");
          }
        } catch (templateError) {
          console.error("Error creating template from shift:", templateError);
          handleResponse("Shift created successfully", "success");
        }
      } else {
        handleResponse("Shift created successfully", "success");
      }

      onSuccess();
    } catch (error) {
      console.error("Error creating shift:", error);
      handleResponse(`Failed to create shift: ${error.message}`, "danger");
    }
  };

  const updateShift = async (data) => {
    try {
      // Calculate week_start from the new date (same logic as createShift)
      const shiftDate = data.date || formatDateToString(new Date());

      // Format activities for backend
      const formattedActivities = activities.map((activity) => {
        // For existing activities (from backend), use the real activity_id
        // For new activities (created in frontend), we need to handle this differently
        let activityId = null;

        // Always use activityId if it exists (this is the actual checkpoint/tour ID)
        if (activity.activityId) {
          // This is an existing activity with a real checkpoint/tour ID
          activityId = activity.activityId;
        } else if (activity.id && typeof activity.id === "number") {
          // For existing activities from the backend, check if this is a real backend ID
          if (activity.id < 1000000000) {
            // This is likely a real backend ID - use it as activity_id
            activityId = activity.id;
          } else {
            // This is a frontend-generated ID (large number), so it's a new activity
            // The activityId should contain the actual checkpoint/tour ID
            activityId = activity.activityId;
          }
        } else {
          // This is a new activity, we'll need to handle this in the backend
          activityId = null;
        }

        const formattedActivity = {
          id: activity.id, // Preserve the original ID for existing activities
          activity_type: activity.type,
          activity_id: activityId,
          label: activity.label,
          start_time: activity.start
            ? convertLocalTimeToUTC(`${shiftDate}T${activity.start}:00`)
            : null,
          end_time: activity.end
            ? convertLocalTimeToUTC(`${shiftDate}T${activity.end}:00`)
            : null,
          is_ad_hoc: activity.adHoc ? 1 : 0,
        };

        return formattedActivity;
      });

      // Calculate week_start from the new date (same logic as createShift)
      const [updateYear, updateMonth, updateDay] = shiftDate
        .split("-")
        .map(Number);
      const selectedDate = new Date(updateYear, updateMonth - 1, updateDay); // month is 0-indexed
      selectedDate.setHours(0, 0, 0, 0);

      const dayOfWeek = selectedDate.getDay();

      // Find the Sunday that starts this week
      if (dayOfWeek !== 0) {
        const daysToSubtract = dayOfWeek;
        selectedDate.setDate(selectedDate.getDate() - daysToSubtract);
      }

      const week_start = selectedDate.toISOString().split("T")[0];

      // Convert local times to UTC before sending to backend
      const startTimeUTC = convertLocalTimeToUTC(
        `${shiftDate}T${data.startTime}:00`,
      );
      const endTimeUTC = convertLocalTimeToUTC(
        `${shiftDate}T${data.endTime}:00`,
      );

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/shift-definitions/${selectedShiftId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: data.name,
            instructions: data.instructions,
            require_geofence: data.require_geofence ? 1 : 0,
            post_id: postIdPayload(data.post_id),
            start_time: startTimeUTC,
            end_time: endTimeUTC,
            week_start: week_start, // Include the week_start so backend can handle schedule association
            activities: formattedActivities,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update shift");
      }

      const updatedShift = await response.json();

      handleResponse("Shift updated successfully", "success");
      onSuccess();
    } catch (error) {
      console.error("Error updating shift:", error);
      handleResponse(`Failed to update shift: ${error.message}`, "danger");
    }
  };

  const handleSave = handleSubmit(onSubmit);

  // State and handler for react-tailwindcss-datepicker
  const [dateValue, setDateValue] = useState({ startDate: "", endDate: "" });
  const handleDateChange = (newValue) => {
    setDateValue(newValue);
    let dateString = newValue.startDate || "";
    if (dateString instanceof Date) {
      // Format as YYYY-MM-DD using local time to avoid timezone issues
      const year = dateString.getFullYear();
      const month = String(dateString.getMonth() + 1).padStart(2, "0");
      const day = String(dateString.getDate()).padStart(2, "0");
      dateString = `${year}-${month}-${day}`;
    }
    if (typeof dateString === "string" && dateString.length > 10) {
      dateString = dateString.slice(0, 10);
    }
    setValue("date", dateString);
    // Trigger validation
    if (dateString) {
      methods.trigger("date");
    }
  };

  // Watch start and end time for logic
  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const startMins = timeToMinutes(startTime);
  const endMins = timeToMinutes(endTime);

  // Activities section: show only hours between start and end
  let activitySlots = [];
  if (isOvernightShift(startTime, endTime)) {
    // For overnight shifts, generate slots from start hour to 23, then 0 to end hour
    const startHour = Math.floor(startMins / 60);
    const endHour = Math.ceil(endMins / 60);

    // Add hours from start to end of day (23)
    for (let h = startHour; h <= 23; h++) {
      activitySlots.push({ label: formatHourLabel(h), isAdHoc: false });
    }
    // Add hours from start of next day (0) to end hour
    for (let h = 0; h <= endHour; h++) {
      activitySlots.push({ label: formatHourLabel(h), isAdHoc: false });
    }
  } else if (startMins < endMins) {
    // Regular shifts within same day
    const startHour = Math.floor(startMins / 60);
    const endHour = Math.ceil(endMins / 60);

    // Add regular hour slots
    for (let h = startHour; h <= endHour; h++) {
      activitySlots.push({ label: formatHourLabel(h), isAdHoc: false });
    }
  }
  activitySlots = [{ label: "Ad hoc", isAdHoc: true }, ...activitySlots];

  // Add/Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // or 'edit'
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [isTemplateSelectionOpen, setIsTemplateSelectionOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen) {
        // Check if the click is on the menu button or menu dropdown
        const menuContainer = event.target.closest(".menu-container");
        if (!menuContainer) {
          setIsMenuOpen(false);
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      if (isMenuOpen) {
        document.removeEventListener("mousedown", handleClickOutside);
      }
    };
  }, [isMenuOpen]);

  // Open modal for add
  const openAddModal = (slot) => {
    setModalMode("add");
    // Store the slot information in editingActivity for the modal
    // Also pass the actual shift start time for proper time handling
    setEditingActivity({
      slot,
      shiftStartTime: startTime,
      shiftEndTime: endTime,
    });
    setEditingActivityId(null);
    setIsModalOpen(true);
  };
  // Open modal for edit
  const openEditModal = (activity) => {
    setModalMode("edit");
    setEditingActivity(activity);
    setEditingActivityId(activity.id);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingActivity(null);
    setEditingActivityId(null);
  };

  // Handle activity deletion
  const handleDeleteActivity = (activityToDelete) => {
    setActivities((prev) => prev.filter((a) => a.id !== activityToDelete.id));
  };

  // Update an activity's start/end (used by drag-to-reschedule on the timeline)
  const handleUpdateActivityTime = (id, start, end) => {
    setActivities((prev) =>
      prev.map((a) => (a.id === id ? { ...a, start, end } : a)),
    );
  };

  // Handle shift deletion
  const handleDeleteShift = () => {
    if (selectedShiftId && !isAddMode) {
      // Only allow deletion for existing shifts, not new ones
      const shiftName = watch("name") || "this shift";
      setSelectedDeleteItem({
        id: selectedShiftId,
        identifier: shiftName,
      });
      setDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = async (shiftId, secondaryId) => {
    try {
      console.log("Attempting to delete shift:", shiftId);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/shift-definitions/${shiftId}`,
        {
          method: "DELETE",
        },
      );

      console.log("Delete response status:", response.status);
      console.log("Delete response headers:", response.headers);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log("Error response data:", errorData);
        throw new Error(
          errorData.error ||
            `Failed to delete shift: ${response.status} ${response.statusText}`,
        );
      }

      const result = await response.json();
      console.log("Delete success result:", result);

      handleResponse("Shift deleted successfully", "success");
      onSuccess();
      toggleOpen();

      return result;
    } catch (error) {
      console.error("Error deleting shift:", error);
      handleResponse(`Failed to delete shift: ${error.message}`, "danger");
      throw error; // Re-throw so DeleteConfirm can handle it
    }
  };

  // Helper: get activities for a slot
  const getActivitiesForSlot = (slot) => {
    // Check if this is the ad hoc slot
    if (slot.isAdHoc) {
      const adHocActivities = activities.filter((activity) => activity.adHoc);
      return adHocActivities;
    }

    // For time-based slots, only return time-based activities that fall within this slot
    const timeBasedActivities = activities.filter((activity) => {
      if (activity.adHoc || !activity.start) {
        return false;
      }

      // Parse the slot time (e.g., "08:00 AM" -> start: "08:00", end: "09:00")
      const slotLabel = slot.label;

      // Convert slot label to 24-hour format for comparison
      const convertTo24Hour = (timeStr) => {
        const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (match) {
          let hour = parseInt(match[1], 10);
          const minute = match[2];
          const ampm = match[3].toUpperCase();

          if (ampm === "PM" && hour !== 12) hour += 12;
          if (ampm === "AM" && hour === 12) hour = 0;

          return `${hour.toString().padStart(2, "0")}:${minute}`;
        }
        return timeStr; // Return as-is if no AM/PM found
      };

      // Convert slot label to 24-hour format
      const slotTime = convertTo24Hour(slotLabel);

      // Calculate slot start and end times (slot represents the hour)
      const [slotHour, slotMinute] = slotTime.split(":").map(Number);
      const slotStart = `${slotHour.toString().padStart(2, "0")}:00`;
      const slotEnd = `${(slotHour + 1).toString().padStart(2, "0")}:00`;

      // Check if activity start time falls within the slot
      const activityStart = activity.start;
      const activityEnd = activity.end || activity.start;

      // Activity is in this slot if:
      // 1. Activity starts within the slot, OR
      // 2. Activity ends within the slot, OR
      // 3. Activity spans the entire slot
      const activityStartsInSlot =
        activityStart >= slotStart && activityStart < slotEnd;
      const activityEndsInSlot =
        activityEnd > slotStart && activityEnd <= slotEnd;
      const activitySpansSlot =
        activityStart <= slotStart && activityEnd >= slotEnd;

      const isInSlot =
        activityStartsInSlot || activityEndsInSlot || activitySpansSlot;

      return isInSlot;
    });

    return timeBasedActivities;
  };

  return (
    <>
      <FormProvider {...methods}>
        <SideSheet
          isOpen={isOpen}
          onClose={toggleOpen}
          title={
            isAddMode
              ? isTemplateMode
                ? "Create template"
                : "Create shift"
              : isTemplateMode
                ? "Template details"
                : "Shift details"
          }
          footerText={
            isAddMode
              ? isTemplateMode
                ? "Create template"
                : "Create shift"
              : isTemplateMode
                ? "Update template"
                : "Update shift"
          }
          handleSave={handleSave}
          size="large"
          secondaryButtonText={!isAddMode ? "Delete" : null}
          secondaryButtonAction={!isAddMode ? handleDeleteShift : null}
          primaryButtonDisabled={!canChange}
          secondaryButtonDisabled={!canChange}
          menuButton={
            (() => {
              return isAddMode && !isTemplateMode;
            })() ? (
              <div className="relative menu-container">
                <button
                  type="button"
                  className="rounded-md text-gray-600 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(!isMenuOpen);
                  }}
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </button>
                <Transition
                  show={isMenuOpen}
                  enter="transition ease-out duration-100 transform"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-75 transform"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                  className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none"
                >
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMenuOpen(false);
                        setIsTemplateSelectionOpen(true);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.06]"
                    >
                      Copy from template
                    </button>
                  </div>
                </Transition>
              </div>
            ) : null
          }
        >
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-6">
            {/* Name */}
            <TextInput name="name" label="Name" disabled={!canChange} />

            {/* Date - Only show for shifts, not templates */}
            {!isTemplateMode && (
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${
                    !canChange
                      ? "text-gray-400 dark:text-gray-500"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  Date
                </label>
                <Datepicker
                  asSingle={true}
                  value={dateValue}
                  onChange={canChange ? handleDateChange : undefined}
                  showTwoCalendars={false}
                  startFrom={selectedDate ? new Date(selectedDate) : new Date()}
                  inputClassName={`h-9 w-full rounded-md px-3 items-center text-sm leading-none tracking-normal font-normal border-0 pr-10 focus:outline-none ${
                    !canChange
                      ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                      : "text-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800" +
                        (errors.date ? " ring-red-300 focus:ring-red-300" : "")
                  }`}
                  placeholder="YYYY-MM-DD"
                  useRange={false}
                  disabled={!canChange}
                  inputName="date"
                  minDate={new Date()} // Disable past dates
                />
                {errors.date && (
                  <p className="mt-2 text-sm text-danger">
                    {errors.date.message}
                  </p>
                )}
              </div>
            )}

            {/* Time - Show for both shifts and templates */}
            <div className="flex space-x-4">
              <div className="flex-1">
                <label
                  className={`block text-xs font-medium mb-1 ${
                    !canChange
                      ? "text-gray-400 dark:text-gray-500"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  Start time
                </label>
                <MultiSelect
                  label="Start time"
                  labelPosition="none"
                  options={toMenuOptions(TIME_OPTIONS)}
                  selected={[startTime]}
                  onUpdate={([value]) => setFormValue("startTime", value)}
                  disabled={!canChange}
                  singleSelect
                  fullWidth
                  matchTriggerWidth
                />
              </div>
              <div className="flex-1">
                <label
                  className={`block text-xs font-medium mb-1 ${
                    !canChange
                      ? "text-gray-400 dark:text-gray-500"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  End time
                </label>
                <MultiSelect
                  label="End time"
                  labelPosition="none"
                  options={toMenuOptions(
                    TIME_OPTIONS.map((t) => ({
                      ...t,
                      disabled: !(
                        isOvernightShift(startTime, t.value) ||
                        timeToMinutes(t.value) > startMins
                      ),
                    })),
                  )}
                  selected={[endTime]}
                  onUpdate={([value]) => setFormValue("endTime", value)}
                  disabled={!canChange}
                  singleSelect
                  fullWidth
                  matchTriggerWidth
                />
              </div>
            </div>

            {/* Customers: narrows the posts and activities to pick from */}
            <SchedulingCustomerFilter
              selected={selectedCustomers}
              onUpdate={handleSelectedCustomers}
              onClear={clearSelectedCustomers}
              fullWidth={true}
              labelPosition="outset"
              matchTriggerWidth={true}
            />

            {/* Post this shift covers, with its activities note. Grouped so the
                sheet's space-y gap doesn't push the note away from the field. */}
            {(showPostPicker || (canChange && postActivities.length > 0)) && (
              <div>
                {showPostPicker && (
                  <MultiSelect
                    label="Post"
                    labelPosition="outset"
                    options={toMenuOptions(
                      postOptions(posts, selectedCustomers, watchedValues.post_id),
                    )}
                    selected={[watchedValues.post_id || ""]}
                    onUpdate={([value]) => setFormValue("post_id", value)}
                    disabled={!canChange}
                    singleSelect
                    fullWidth
                    matchTriggerWidth
                  />
                )}
                {canChange && postActivities.length > 0 && (
                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    {missingPostActivities.length === 0 ? (
                      postActivities.length === 1 ? (
                        "This post's activity is on this shift."
                      ) : (
                        `All ${postActivities.length} of this post's activities are on this shift.`
                      )
                    ) : (
                      <>
                        {missingPostActivities.length === postActivities.length
                          ? postActivities.length === 1
                            ? "This post has 1 activity."
                            : `This post has ${postActivities.length} activities.`
                          : `${missingPostActivities.length} of this post's activities aren't on this shift.`}{" "}
                        <button
                          type="button"
                          onClick={addPostActivities}
                          className="font-medium text-brand-primary hover:underline"
                        >
                          {missingPostActivities.length ===
                          postActivities.length
                            ? missingPostActivities.length === 1
                              ? "Add it to this shift"
                              : "Add them to this shift"
                            : "Add the rest"}
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Instructions */}
            <TextArea
              name="instructions"
              label="Instructions"
              disabled={!canChange}
            />

            {/* Require clock-in geofence (checkbox + status grouped so the
                page's space-y doesn't push the status away from the checkbox) */}
            <div>
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="require_geofence"
                  name="require_geofence"
                  className="h-4 w-4 mt-0.5 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                  disabled={!canChange}
                  {...methods.register("require_geofence")}
                />
                <label
                  htmlFor="require_geofence"
                  className={`ml-2 block text-sm ${
                    !canChange
                      ? "text-gray-400 dark:text-gray-500"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  Require the guard to be on site to clock-in
                </label>
              </div>

              {/* Where clock-in will be checked — indented under the label */}
              {watchedValues.require_geofence &&
                (() => {
                  // A post on a site decides where clock-in is checked;
                  // otherwise (patrol route or no post) the first activity does.
                  const primary = selectedPost?.site
                    ? selectedPost.site
                    : resolvePrimarySite(activities);
                  return (
                    <div className="ml-6">
                      <GeofenceStatus
                        siteId={primary?.id || null}
                        siteName={primary?.name || null}
                      />
                    </div>
                  );
                })()}
            </div>

            {/* Template Checkbox - Only show in add mode from schedules view */}
            {isAddMode && !isTemplateMode && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="saveAsTemplate"
                  name="saveAsTemplate"
                  className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                  disabled={!canChange}
                  {...methods.register("saveAsTemplate")}
                />
                <label
                  htmlFor="saveAsTemplate"
                  className={`ml-2 block text-sm ${
                    !canChange
                      ? "text-gray-400 dark:text-gray-500"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  Save as shift template
                </label>
              </div>
            )}

            {/* Activities Section */}
            <div className="mt-6">
              <div className="font-semibold text-sm mb-2 text-gray-800 dark:text-gray-100">
                Activities
              </div>
              <ActivitiesList
                activitySlots={activitySlots}
                getActivitiesForSlot={getActivitiesForSlot}
                openAddModal={openAddModal}
                openEditModal={openEditModal}
                selectedActivityId={isModalOpen ? editingActivityId : null}
                onDeleteActivity={handleDeleteActivity}
                canChange={canChange}
                activities={activities}
                shiftStartTime={startTime}
                shiftEndTime={endTime}
                onUpdateActivityTime={handleUpdateActivityTime}
              />
            </div>
          </div>
        </SideSheet>
      </FormProvider>
      <ActivityDetails
        isOpen={isModalOpen}
        mode={modalMode}
        editingActivity={editingActivity}
        selectedActivityId={editingActivityId}
        closeModal={closeModal}
        timeOptions={TIME_OPTIONS}
        shiftStartTime={startTime}
        shiftEndTime={endTime}
        customerIds={selectedCustomers}
        onSave={(activityData) => {
          setActivities((prev) => {
            let newActivities;
            if (modalMode === "edit") {
              newActivities = prev.map((a) =>
                a.id === activityData.id ? activityData : a,
              );
            } else {
              newActivities = [...prev, activityData];
            }

            // Sort activities by start time (ad hoc activities first, then by start time)
            const sortedActivities = newActivities.sort((a, b) => {
              // Ad hoc activities come first
              if (a.adHoc && !b.adHoc) return -1;
              if (!a.adHoc && b.adHoc) return 1;

              // If both are ad hoc, maintain original order
              if (a.adHoc && b.adHoc) return 0;

              // For timed activities, sort by start time
              if (a.start && b.start) {
                return a.start.localeCompare(b.start);
              }

              // If one has start time and other doesn't, put the one with start time first
              if (a.start && !b.start) return -1;
              if (!a.start && b.start) return 1;

              return 0;
            });

            return sortedActivities;
          });

          // Close modal after state update
          closeModal();
        }}
      />

      <TemplateSelection
        isOpen={isTemplateSelectionOpen}
        onClose={() => {
          setIsTemplateSelectionOpen(false);
        }}
        onTemplateSelect={(template) => {
          // Map template activities to the format expected by the form
          const mappedActivities = (template.activities || []).map((a) => ({
            id: Date.now() + Math.random(), // Generate a unique frontend ID
            activityId: a.activity_id, // This is the actual checkpoint/tour ID
            type: a.activity_type,
            label: a.label,
            adHoc: a.is_ad_hoc === 1,
            start: a.start_time ? parseDateTime(a.start_time).time : null,
            end: a.end_time ? parseDateTime(a.end_time).time : null,
            duration: a.duration_minutes,
          }));

          // Update form with template data (carry the template's geofence
          // default onto the new shift; it stays editable as a per-shift override)
          reset({
            name: template.name || "",
            date: selectedDate || formatDateToString(new Date()),
            startTime: parseDateTime(template.start_time).time,
            endTime: parseDateTime(template.end_time).time,
            instructions: template.instructions || "",
            require_geofence: !!template.require_geofence,
            post_id: template.post_id ? String(template.post_id) : "",
          });

          // Update date picker value to match the form
          setDateValue({
            startDate: selectedDate || formatDateToString(new Date()),
            endDate: selectedDate || formatDateToString(new Date()),
          });

          // Update activities
          setActivities(mappedActivities);

          // Close template selection
          setIsTemplateSelectionOpen(false);
        }}
      />

      {selectedDeleteItem?.id && (
        <DeleteConfirm
          isOpen={deleteModalOpen}
          toggleOpen={() => setDeleteModalOpen(false)}
          selectedDeleteItem={selectedDeleteItem}
          handleDeleteSuccess={() => {
            setDeleteModalOpen(false);
            setSelectedDeleteItem({ id: null, identifier: "" });
          }}
          onDelete={handleDeleteConfirm}
        />
      )}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={hideToast}
      />
    </>
  );
}

export { ShiftDetails };
